import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable from "./components/SelectableEnrollmentTable";

interface BulkIdCardModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
}

const BulkIdCardModal: React.FC<BulkIdCardModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
}) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [doneUrl, setDoneUrl] = useState<string | null>(null);

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const handleClose = () => {
    setError("");
    setDoneUrl(null);
    onHide();
  };

  const handleGenerate = async () => {
    setError("");
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setGenerating(true);
    try {
      const resp = await EnrollmentService.bulkIdCard({
        enrollmentIds: selectedEnrollmentIds,
      });
      const url = resp.data?.data?.presignedUrl;
      if (url) {
        window.open(url, "_blank");
        setDoneUrl(url);
      } else {
        setError("ID cards were generated but no download URL was returned.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to generate ID cards");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Generate ID Cards (Bulk)</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {doneUrl ? (
          <div
            style={{
              padding: 16,
              background: "var(--status-success-bg)",
              color: "var(--status-success-text)",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            ID cards generated. If the PDF didn't open,{" "}
            <a href={doneUrl} target="_blank" rel="noreferrer">
              click here to download
            </a>
            .
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "var(--text-body)", marginBottom: 10 }}>
              Generates one ID card per selected enrollment and combines them into a single PDF.
            </div>

            <SelectableEnrollmentTable
              enrollments={enrollments}
              selectedIds={selectedIds}
              allSelected={allSelected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              disabled={generating}
              caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={generating}>
          {doneUrl ? "Close" : "Cancel"}
        </Button>
        {!doneUrl && (
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={generating || selectedEnrollmentIds.length === 0}
          >
            {generating ? (
              <>
                <i className="fa fa-spinner fa-spin" /> Generating…
              </>
            ) : (
              <>Generate PDF ({selectedEnrollmentIds.length})</>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkIdCardModal;
